/**
 * The Bank tab: the cash proof, night by night.
 *
 * Every night the daily control asks one question per account — does the balance
 * the bank reports equal the balance our ledger derives, and is every movement
 * on either side explained by something on the other. This is the history of
 * that answer, which until now existed only as rows nobody could see and an
 * alert nobody could look up afterwards.
 *
 * Read-only on purpose. A screen that let someone re-run the proof until it came
 * out green would be the opposite of a control.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import opsService, { type ReconciliationRun } from '../../services/ops.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import { describeProof, needsAttention, type ProofTone } from './ops.helpers';

const TONE: Record<ProofTone, BadgeTone> = { ok: 'success', warn: 'warning', info: 'neutral' };

export default function OpsBankTab(): JSX.Element {
  const [runs, setRuns] = useState<ReconciliationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await opsService.reconciliationRuns());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the cash proof.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const attention = runs.filter(needsAttention).length;

  return (
    <div>
      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Each row is one night, one account: what the bank said it held, what our ledger
        derives, and whether every movement on one side is explained by the other.
        {attention > 0 && ` ${attention} of these need someone to look.`}
      </p>

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && runs.length === 0 && (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          The cash proof has not run yet. It runs nightly with the daily control.
        </div>
      )}

      <ul className="space-y-3">
        {runs.map((r) => <ProofRow key={r.id} run={r} />)}
      </ul>
    </div>
  );
}

function ProofRow({ run }: Readonly<{ run: ReconciliationRun }>): JSX.Element {
  const [open, setOpen] = useState(false);
  const verdict = describeProof(run);
  const hasFindings = run.findings.length > 0;

  return (
    <li className="rounded-2xl p-4"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {run.account_label ?? run.provider} · {formatDateTime(run.as_of)}
          </div>
          <p className="text-sm mt-0.5" style={{ color: colors.inkSoft }}>{verdict.text}</p>
          <div className="text-xs mt-1" style={{ color: colors.muted }}>
            Bank {run.bank_balance} {run.currency_code} · ledger {run.derived_balance} {run.currency_code}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge tone={TONE[verdict.tone]}>{run.status}</StatusBadge>
          {hasFindings && (
            <button type="button" onClick={() => setOpen((v) => !v)}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              {open ? 'Hide detail' : `${run.findings.length} finding(s)`}
            </button>
          )}
        </div>
      </div>

      {open && hasFindings && (
        <ul className="mt-3 pt-3 border-t space-y-1" style={{ borderColor: colors.border }}>
          {run.findings.map((f, i) => (
            <li key={`${run.id}-${i}`} className="text-xs font-mono"
                style={{ color: colors.inkSoft }}>
              {JSON.stringify(f)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
